import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { CloseIcon, FileIcon, SearchIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { cast, handleApiError, notify } from '~/utils/helper';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { IJobFile } from '~/utils/interface/job';
import { BaseImage, BaseSelect } from '~/components/common';
import { FileTypeEnum } from '~/utils/constants/file';
import { IFolder } from '~/utils/interface/folder';
import { ITag } from '~/utils/interface/tag';
import { handleUpdateFile } from '~/thunks/file/fileThunk';

type EditFileDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IJobFile | undefined;
  folders?: Array<IFolder>;
  id?: string;
};

type UploadFiles = {
  folder?: string;
  tags?: ITag[];
  note?: string;
  name?: string;
};
const initialValues: UploadFiles = {};
const EditFileDialog = (props: EditFileDialogProps) => {
  const { t } = useTranslation(['admin/job']);
  const { data, folders } = props;
  const dispatch = useAppDispatch();
  const [fileExtension, setFileExtension] = useState<string>('');

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
    form.resetForm();
  };

  const handleCancel = () => {
    handleSetOpen(false);
  };
  const { tags } = useAppSelector((state) => state.tag);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      try {
        const tagIds = values.tags?.map((tag) => tag.id);
        values.name = values.name + '.' + fileExtension;
        const { tags, ...otherValues } = values;

        const dataToSubmit = {
          ...otherValues,
          tagIds: tagIds,
        };

        data?.id && (await dispatch(handleUpdateFile({ id: data.id, data: dataToSubmit })).unwrap());
        notify('success', t('editSuccess', { ns: 'admin/common' }));
        handleSetOpen(false, true);
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });
  const selectedTags = form.values.tags || [];
  useEffect(() => {
    if (data) {
      const fileExtension = data?.name ? data.name.split('.')[1] : '';
      setFileExtension(fileExtension);
      form.setFieldValue('name', cast(data.name.split('.')[0]));
      form.setFieldValue('folder', cast(data.folder?.id));
      form.setFieldValue('note', cast(data.note));
      form.setFieldValue('tags', cast(data.tags));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='md'>
      <DialogTitle>
        {t('editPrice')}
        <IconButton onClick={() => handleSetOpen(false)} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box className='pb-[15px] flex w-1/4 items-center'>
          <Typography className='pr-2'>{t('estimate.fileItem.folder')}</Typography>
          <BaseSelect
            name='folder'
            value={form.values?.folder}
            options={folders}
            onChange={form.handleChange}
            error={Boolean(form.errors?.folder)}
            helperText={form.errors?.folder}
          ></BaseSelect>
        </Box>
        <Box className='pb-[15px]'>
          <InputLabel>
            {t('estimate.fileItem.tags')}
            <span className=''> {t('estimate.fileItem.tagNote')}</span>
          </InputLabel>
          <Autocomplete
            multiple
            disableClearable
            value={form.values.tags || []}
            onChange={(event, newValue) => form.setFieldValue('tags', newValue)}
            options={tags}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            popupIcon={null}
            renderTags={() => null}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                variant='outlined'
                placeholder={t('searchPlaceholder')}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  endAdornment: (
                    <>
                      <SearchIcon className='w-[16px] mr-[1px]' />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <div className='mt-[5px]'>
            {selectedTags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                onDelete={() => {
                  const newTags = selectedTags.filter((t) => t.id !== tag.id);
                  form.setFieldValue('tags', newTags);
                }}
                className='mr-[10px]'
              />
            ))}
          </div>
        </Box>
        <Box>
          <Box className='w-full'>
            <Box className='border-2 border-solid my-2 relative'>
              <Box className='flex justify-flex-start w-full'>
                <Box onClick={() => window.open(data?.url)} className='w-[100px] h-[100px]'>
                  {data?.type === FileTypeEnum.IMAGE ? (
                    <BaseImage src={data?.url} className='w-full h-full object-cover' />
                  ) : (
                    <FileIcon className='mx-auto my-[15px] w-[70px] h-[70px]' />
                  )}
                </Box>
                <Box className='block border-l-2 px-[50px] flex-1'>
                  <Box className='flex mt-3 w-full'>
                    <InputLabel className='w-[100px] content-center	'> {t('estimate.fileItem.name')}</InputLabel>
                    <TextField
                      name={`name`}
                      value={cast(form.values.name)}
                      onChange={form.handleChange}
                      InputProps={{
                        endAdornment: <InputAdornment position='end'>.{fileExtension}</InputAdornment>,
                      }}
                    />
                  </Box>
                  <Box className='flex mt-3 w-full'>
                    <InputLabel className='w-[100px] content-center'>{t('estimate.fileItem.note')}</InputLabel>
                    <TextField name={`note`} value={cast(form.values.note)} type='text' onChange={form.handleChange} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button color='secondary' variant='contained' onClick={handleCancel}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditFileDialog);
